import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import sortBy from 'lodash.sortby';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation } from '@hooks';
import { analyticsService } from '@services';
import ExportCSV from '@features/Analytics/ExportCSV';
import { GetTableBody } from './GetTableBody';
import { getCollapsibleTableBody } from './CollapsibleRowBody';

export const PaperStyled = styled(Paper)({
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: '4px',
    '&:not(:last-child)': {
        marginBottom: 16,
    },
});

export const TableStyled = styled(Table)({
    '& tbody .MuiTableRow-root[data-row=true]': {
        backgroundColor: 'rgba(247, 251, 249)',
    },
});

export const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

export const Text = styled('span', { shouldForwardProp: prop => prop !== 'withIcon' })(({ withIcon }) => ({
    width: 'auto',
    marginLeft: '4px',
    color: 'rgba(0, 0, 0, 0.6)',
    marginRight: '28px',
    ...(withIcon && {
        marginRight: '8px',
    }),
}));

export const GetTableHeader = isQr => {
    const { t } = useTranslation();
    const width = isQr ? '18%' : '14%';
    return [
        {
            key: 0,
            component: <></>,
            width: '5%',
        },
        {
            key: 1,
            component: (
                <Typography variant="subtitle2" align="left" sx={{ minWidth: '100px' }}>
                    {t('common.location')}
                </Typography>
            ),
            width: '100px',
        },
        {
            key: 2,
            component: (
                <Typography variant="subtitle2" align="center" sx={{ minWidth: '89px' }}>
                    {isQr ? t('AnalyticsConversion.openedSurveys') : t('AnalyticsConversion.sentSurveys')}
                </Typography>
            ),
            width,
        },
        ...(!isQr
            ? [
                  {
                      key: 3,
                      component: (
                          <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                              {t('AnalyticsConversion.openedSurveys')}
                          </Typography>
                      ),
                      width,
                  },
              ]
            : []),
        {
            key: 4,
            component: (
                <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                    {t('AnalyticsConversion.responses')}
                </Typography>
            ),
            width,
        },
        {
            key: 5,
            component: (
                <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                    {t('AnalyticsConversion.proceededToReviewSite')}
                </Typography>
            ),
            width,
        },
        {
            key: 6,
            component: (
                <Typography align="center" variant="subtitle2" sx={{ minWidth: '89px' }}>
                    {t('AnalyticsConversion.collectedReviews')}
                </Typography>
            ),
            width,
        },
    ];
};

export const getTableArrow = field => {
    if (field > 0) {
        return <ArrowUpwardIcon sx={{ color: '#0B8D00', fontSize: '20px !important' }} />;
    } else if (field < 0) {
        return <ArrowDownwardIcon sx={{ color: '#B00020', fontSize: '20px !important' }} />;
    }
    return null;
};

const CollapsibleRow = ({ data, isQr, index }) => {
    return <Table rows={getCollapsibleTableBody(data, index, isQr)} />;
};

const ConversionTable = ({ isLoading, data, filter, type }) => {
    const locations = useSelector(state => state.account?.account?.companies || []);
    const surveys = useSelector(state => state.surveys.data);
    const { t } = useTranslation();

    const onExport = exportType =>
        analyticsService.exportAnalyticsCSV(
            {
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            },
            'conversion',
            exportType,
        );

    const isQr = type === 'qr';

    const dataSortedByName = useMemo(() => {
        const total = data.find(item => item.id === null && item.surveyTemplateId === null);
        const dataWithLocationNames = data
            .filter(item => item.id !== null || item.surveyTemplateId !== null)
            .map(item => ({
                ...item,
                internalName: locations.find(location => location.companyId === item.id)?.internalName || '',
                surveyName: surveys.find(s => s.survey.id === item.surveyTemplateId)?.survey?.name || '',
            }));
        return [...sortBy(dataWithLocationNames, item => item.internalName.toLowerCase()), ...(total ? [total] : [])];
    }, [data, locations, surveys]);

    return (
        <PaperStyled elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`AnalyticsConversion.${isQr ? 'qrCodes' : 'surveyInvitations'}`)}
                </Typography>
                <ExportCSV type={isQr ? 'qr' : 'surveys'} apiCall={() => onExport(isQr ? 'qr' : 'surveys')} />
            </div>
            <TableContainer>
                <TableStyled
                    data={dataSortedByName}
                    columnNames={GetTableHeader(isQr)}
                    rows={GetTableBody(dataSortedByName, isQr)}
                    isDataLoading={isLoading}
                    isCollapsible={true}
                    CollapsibleRow={props => <CollapsibleRow {...props} isQr={isQr} />}
                    withTotalRow
                />
            </TableContainer>
        </PaperStyled>
    );
};

export default ConversionTable;
